import linkIcon from "@loot-os/icons/icons/link";
import { Badge } from "../../../../../core/Badge.js";
import { Button } from "../../../../../core/Button.js";
import { cardStyles } from "../../../../../core/Card.styles.js";
import { Skeleton } from "../../../../../core/Skeleton.js";
import { Text } from "../../../../../core/Text.js";
import { Amount } from "../../../../../domain/Amount.js";
import { BattleRoundsStrip } from "../../../../../domain/battles/BattleRoundsStrip.js";
import { cn } from "../../../../../foundations/cn.js";
import { FOCUS_RING } from "../../../../../foundations/interaction.js";
import { useChromeHints } from "../../../../shared/chromeHints.js";
import { LoadError } from "../../../../shared/LoadError.js";
import type { BattleRoomHeaderContent } from "../../../battlesContent.js";

/**
 * The battle's head: its mode and switches over the title, Copy link, then the info bar (the
 * round showing of all of them, the entry, and the boxes in round order with the current one
 * lifted). While the battle loads, or if it can't, the head says so in its place.
 */
export function BattleRoomHeader({ back, state }: BattleRoomHeaderContent) {
  useChromeHints({ back });
  if (state.status === "loading") {
    return (
      <div aria-busy className="flex flex-col gap-4">
        <Skeleton className="h-10 w-64 rounded-lg" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }
  if (state.status === "error") return <LoadError message={state.message} />;
  const { title, tags, rounds, entry, copyLink } = state;
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-2">
          {tags.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <li key={tag.label} title={tag.hint}>
                  <Badge tone={tag.tone ?? "neutral"}>{tag.label}</Badge>
                  {tag.hint && <span className="sr-only">: {tag.hint}</span>}
                </li>
              ))}
            </ul>
          )}
          <Text as="h1" variant="display-xl2" tone="primary" className="max-md:text-display-xl3">
            {title}
          </Text>
        </div>
        <Button variant="secondary" size="sm" icon={linkIcon} onClick={copyLink.onCopy}>
          {copyLink.label}
        </Button>
      </div>
      <div
        className={cardStyles({
          radius: "2xl",
          className: "flex flex-wrap items-center gap-x-8 gap-y-3 px-5 py-3",
        })}
      >
        <dl className="flex items-center gap-8">
          <div className="flex flex-col">
            <dt className="order-2 font-body text-caption-eyebrow text-text-tertiary uppercase">
              {rounds.label}
            </dt>
            <dd className="font-mono text-mono-price text-text-primary">
              {rounds.current}
              <span className="text-text-tertiary">/{rounds.total}</span>
            </dd>
          </div>
          <div className="flex flex-col">
            <dt className="order-2 font-body text-caption-eyebrow text-text-tertiary uppercase">
              {entry.label}
            </dt>
            <dd>
              <Amount cents={entry.cents} className="text-text-primary" />
            </dd>
          </div>
        </dl>
        <section
          aria-label={rounds.label}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: a strip that scrolls must be reachable (WCAG 2.1.1).
          tabIndex={0}
          className={cn(
            "flex min-w-0 flex-1 overflow-x-auto rounded-md py-1 md:justify-center",
            FOCUS_RING,
          )}
        >
          <BattleRoundsStrip
            rounds={rounds.boxes}
            current={rounds.active}
            max={12}
            roundLabel={rounds.roundLabel}
          />
        </section>
      </div>
    </div>
  );
}
